/**
 * @fileoverview Orders table with sorting, pagination, loading and error states.
 * @module features/orders/components/OrdersTable
 */

import { ReactElement, useEffect, useMemo, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { cn } from '@/shared/lib/utils'
import { useFilter } from '@/shared/hooks/useFilter'
import { salesService, type SalesParams } from '@/features/orders/api/salesService'
import type { SaleDTO } from '@/features/orders/schemas/salesSchema'
import { Button, Money } from '@/shared/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { listStatusMappings } from '@/features/status-mappings/api/statusMappingsService'
import { toast } from 'sonner'
import { Package } from 'lucide-react'
import { codeToFlag } from '@/shared/utils/countryUtils'

// Hook to resolve status IDs to human-readable names
function useStatusNames(): (statusId: string) => string {
  const { data: mappings = [] } = useQuery({
    queryKey: ['status-mappings'],
    queryFn: () => listStatusMappings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  return useMemo(() => {
    const map = new Map(mappings.map(m => [m.externalStatus, m.statusName ?? m.externalStatus]))
    return (statusId: string) => map.get(statusId) ?? statusId
  }, [mappings])
}

/**
 * Columns we support sorting by. Pass-through to backend.
 */
type SortField =
  | 'id'
  | 'orderId'
  | 'date'
  | 'sku.name'
  | 'country'
  | 'quantity'
  | 'reportingUnitPrice'
  | 'reportingTotal'
  | 'status'
  | 'statusDate'

export function OrdersTable(): ReactElement {
  const { t } = useTranslation('features-sales')
  const getStatusName = useStatusNames()
  // Async and data state
  const [sales, setSales] = useState<SaleDTO[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination
  const [totalItems, setTotalItems] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(0) // zero-based
  const [pageSize, setPageSize] = useState<number>(50)
  const [totalPages, setTotalPages] = useState<number>(0)

  // Sorting
  const [sortField, setSortField] = useState<SortField>('date')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Filters from context
  const { startDate, endDate, countries, skus } = useFilter()

  const queryParams: SalesParams = useMemo(() => {
    const p: SalesParams = {
      page: currentPage,
      size: pageSize,
      sort: sortField,
      direction: sortDirection,
    }
    if (startDate) p.startDate = startDate
    if (endDate) p.endDate = endDate
    if (countries.length > 0) p.countries = countries
    if (skus.length > 0) p.skus = skus
    return p
  }, [currentPage, pageSize, sortField, sortDirection, startDate, endDate, countries, skus])

  useEffect(() => {
    let cancelled = false
    const run = async (): Promise<void> => {
      try {
        setLoading(true)
        setError(null)
        const res = await salesService.getSales(queryParams)
        if (cancelled) return
        setSales(res.content)
        setTotalItems(res.totalElements)
        setTotalPages(res.totalPages)
      } catch (e) {
        if (cancelled) return
        console.error('[SalesTable] fetch error', e)
        setError(t('toasts.loadError'))
        toast.error(t('toasts.loadError'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [queryParams, t])

  const onSort = (field: SortField): void => {
    if (field === sortField) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setCurrentPage(0)
  }

  const renderSortIndicator = (field: SortField): string | null => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? ' ↑' : ' ↓'
  }

  const canPrev = currentPage > 0
  const canNext = currentPage < totalPages - 1

  const showingFrom = sales.length > 0 ? currentPage * pageSize + 1 : 0
  const showingTo = Math.min((currentPage + 1) * pageSize, totalItems)

  // Group sales by Order ID
  const groupedByOrderId = useMemo(() => {
    const grouped = new Map<string, SaleDTO[]>()
    sales.forEach(sale => {
      const orderId = sale.orderId
      if (!grouped.has(orderId)) {
        grouped.set(orderId, [])
      }
      grouped.get(orderId)?.push(sale)
    })
    return Array.from(grouped.entries())
  }, [sales])

  return (
    <div className="max-w-full overflow-x-auto flex-1">
      {error && (
        <div className="mb-3 p-3 text-sm bg-destructive/10 text-destructive border border-destructive/20 rounded-md">
          {error}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className="pl-6 cursor-pointer text-xs font-medium text-muted-foreground uppercase tracking-wide"
              onClick={() => onSort('orderId')}
            >
              {t('headers.orderId')}
              {renderSortIndicator('orderId')}
            </TableHead>
            <TableHead
              className="pl-6 cursor-pointer text-xs font-medium text-muted-foreground uppercase tracking-wide"
              onClick={() => onSort('sku.name')}
            >
              {t('headers.items')}
              {renderSortIndicator('sku.name')}
            </TableHead>
            <TableHead
              className="pl-6 cursor-pointer text-xs font-medium text-muted-foreground uppercase tracking-wide"
              onClick={() => onSort('reportingTotal')}
            >
              {t('headers.amount')}
              {renderSortIndicator('reportingTotal')}
            </TableHead>
            <TableHead
              className="pl-6 cursor-pointer text-xs font-medium text-muted-foreground uppercase tracking-wide border-l border-border/40"
              onClick={() => onSort('reportingTotal')}
            >
              {t('headers.total')}
              {renderSortIndicator('reportingTotal')}
            </TableHead>
            <TableHead
              className="pl-6 cursor-pointer text-xs font-medium text-muted-foreground uppercase tracking-wide border-l border-border/40"
              onClick={() => onSort('status')}
            >
              {t('headers.status')}
              {renderSortIndicator('status')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-6">
                {t('states.loading')}
              </TableCell>
            </TableRow>
          ) : sales.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-6">
                {t('states.empty')}
              </TableCell>
            </TableRow>
          ) : (
            groupedByOrderId.map(([orderId, groupedSales]) => {
              const firstSale = groupedSales[0]!
              const orderTotal = groupedSales.reduce((sum, sale) => sum + sale.reportingTotal, 0)
              return (
                <React.Fragment key={orderId}>
                  {groupedSales.map((sale, idx) => (
                    <TableRow
                      key={sale.id}
                      className={cn(
                        'hover:bg-accent/40',
                        idx < groupedSales.length - 1 && 'border-b-0'
                      )}
                    >
                      {idx === 0 ? (
                        <TableCell className="pl-6 align-top" rowSpan={groupedSales.length}>
                          <div className="flex items-start gap-2">
                            <span className="text-lg shrink-0" aria-hidden>
                              {codeToFlag(firstSale.country)}
                            </span>
                            <div className="flex flex-col gap-1">
                              <div className="text-xs text-foreground">{orderId}</div>
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(firstSale.date), 'dd LLL yyyy')}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      ) : null}
                      <TableCell className="pl-6 border-l border-border/40">
                        <div className="flex items-start gap-3">
                          {sale.sku.imageUrl ? (
                            <img
                              src={sale.sku.imageUrl}
                              alt={sale.sku.productName || sale.sku.name}
                              className="size-14 rounded-md object-cover border border-border shrink-0"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="size-14 rounded-md border border-dashed border-border/60 flex items-center justify-center text-muted-foreground shrink-0">
                              <Package className="size-5" />
                            </div>
                          )}
                          <div className="flex flex-col gap-1 min-w-0">
                            <div className="text-foreground font-medium truncate">
                              {sale.sku.systemSku
                                ? (sale.sourceProductName || sale.sku.productName || sale.sku.name)
                                : (sale.sku.productName || sale.sku.name)}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {sale.quantity} × {sale.sku.name}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="pl-6 text-muted-foreground font-medium">
                        <Money
                          value={sale.reportingTotal}
                          currency={sale.reportingCurrency}
                          minFraction={2}
                          maxFraction={2}
                          useGrouping={false}
                        />
                      </TableCell>
                      {/* Show Order Total only on first row of the group */}
                      {idx === 0 ? (
                        <TableCell
                          className="pl-6 border-l border-border/40 align-middle text-foreground font-semibold"
                          rowSpan={groupedSales.length}
                        >
                          <Money
                            value={orderTotal}
                            currency={firstSale.reportingCurrency}
                            minFraction={2}
                            maxFraction={2}
                            useGrouping={false}
                          />
                        </TableCell>
                      ) : null}
                      {/* Show Status and Status Date on first row of the group */}
                      {idx === 0 ? (
                        <TableCell
                          className="pl-6 border-l border-border/40 align-middle"
                          rowSpan={groupedSales.length}
                        >
                          <div className="flex flex-col gap-1">
                            <div className="text-foreground font-medium">
                              {getStatusName(firstSale.status)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(firstSale.statusDate), 'dd LLL yyyy')}
                            </div>
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </React.Fragment>
              )
            })
          )}
        </TableBody>
      </Table>
      {!loading && totalPages > 0 && (
        <div className="flex items-center justify-between border-t border-border p-3 mt-2">
          <div className="text-sm text-muted-foreground">
            {t('pagination.showing')}{' '}
            <span className="font-medium text-foreground">{showingFrom}</span> {t('pagination.to')}{' '}
            <span className="font-medium text-foreground">{showingTo}</span> {t('pagination.of')}{' '}
            <span className="font-medium text-foreground">{totalItems}</span>{' '}
            {t('pagination.results')}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
                setCurrentPage(0)
              }}
              className="border border-input bg-background text-foreground rounded-md text-sm h-9 px-2"
              aria-label={t('pagination.rowsPerPage')}
            >
              <option value={50}>{t('pagination.perPage', { count: 50 })}</option>
              <option value={100}>{t('pagination.perPage', { count: 100 })}</option>
            </select>
            <div className="flex items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={!canPrev}
                aria-label={t('pagination.prev')}
              >
                ←
              </Button>
              <div className="px-2 text-sm">
                {t('pagination.pageXofY', {
                  current: currentPage + 1,
                  total: Math.max(1, totalPages),
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={!canNext}
                aria-label={t('pagination.next')}
              >
                →
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
